import {
  collectOrgSubtree,
  type OrgTreeNode,
  type ScopeVerdict,
} from "@/lib/org-structure-tree";

/**
 * Ancestrais de `node`, pelo eixo de ESCOPO.
 *
 * Não reusa `computeOrgRunClosure`: aquele é do eixo de EXECUÇÃO e pula
 * ancestral `completed`, que é exatamente o nó que o servidor
 * (`ancestors_of`) aprova sem pular. Os dois eixos são separados por decisão
 * da #845, e misturá-los fazia o modal prometer menos do que ia acontecer.
 */
/** Nó por chave na árvore canônica. Lote grande monta uma vez e repassa a `previewCascade`. */
export function indexOrgForest(roots: OrgTreeNode[]): Map<string, OrgTreeNode> {
  const byKey = new Map<string, OrgTreeNode>();
  const index = (nodes: OrgTreeNode[]) => {
    for (const current of nodes) {
      byKey.set(current.key, current);
      index(current.children);
    }
  };
  index(roots);
  return byKey;
}

function scopeAncestors(node: OrgTreeNode, byKey: Map<string, OrgTreeNode>): OrgTreeNode[] {

  const chain: OrgTreeNode[] = [];
  const seen = new Set<string>([node.key]);
  let current = node.parent ? byKey.get(node.parent.key) : undefined;
  while (current !== undefined && !seen.has(current.key)) {
    seen.add(current.key);
    chain.push(current);
    current = current.parent ? byKey.get(current.parent.key) : undefined;
  }
  return chain;
}

/**
 * O que MAIS será afetado, além da unidade clicada, se o veredito for gravado.
 *
 * Serve só para o modal informar antes de confirmar: o servidor recalcula e
 * impõe (Task 5). Calcular aqui evita uma ida ao servidor só para abrir o
 * modal.
 *
 * `computeOrgRunClosure`/`collectOrgSubtree` filtram por `migrationStatus`
 * (execução) — um eixo diferente de `scopeVerdict` (escopo). Por isso o
 * filtro por veredito abaixo é adicional, não substitui a filtragem delas: um
 * ancestral já executado (`completed`) nem entra no resultado de
 * `computeOrgRunClosure`; um ancestral não-executado mas já no veredito-alvo
 * é removido aqui.
 *
 * Roda sobre a árvore canônica (`roots`), nunca sobre a filtrada da exibição
 * — o filtro de exibição remove nós e faria a prévia anunciar menos efeito do
 * que vai acontecer de verdade. Foi exatamente esse o bug encontrado no code
 * review da Parte 1 (#773) para o cálculo de fecho de execução.
 */
export function previewCascade(
  node: OrgTreeNode,
  verdict: ScopeVerdict,
  roots: OrgTreeNode[],
  byKey?: Map<string, OrgTreeNode>,
): OrgTreeNode[] {
  const index = () => byKey ?? indexOrgForest(roots);
  if (verdict === "pending") {
    const related = node.scopeVerdict === "approved" ? collectOrgSubtree(node)
      : node.scopeVerdict === "out_of_scope" ? scopeAncestors(node, index()) : [];
    return related.filter((candidate) => candidate.key !== node.key && candidate.scopeVerdict === node.scopeVerdict);
  }
  const affected =
    verdict === "approved" ? scopeAncestors(node, index()) : collectOrgSubtree(node);

  return affected.filter(
    (candidate) => candidate.key !== node.key && candidate.scopeVerdict !== verdict,
  );
}
